import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { RefObject, useEffect, useRef } from 'react';

type AnimationType = 'fadeIn' | 'scaleUp' | 'slideUp' | 'header' | 'stopMove';

interface AnimationOptions {
  trigger?: RefObject<HTMLElement>;
  start?: string;
  end?: string;
  scrub?: boolean | number;
  pin?: boolean;
  delay?: number;
  from?: {
    x?: number;
    y?: number;
    opacity?: number;
    scale?: number;
    xPercent?: number;
  };
  to?: {
    x?: number;
    y?: number;
    opacity?: number;
    scale?: number;
    duration?: number;
    delay?: number;
    ease?: string;
    xPercent?: number;
  };
}

export function useAnimatedRef<T extends HTMLElement>(
  type: AnimationType,
  options: AnimationOptions = {},
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current) {
      return undefined;
    }

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const element = ref.current;
      if (!element) {
        return;
      }

      switch (type) {
        case 'fadeIn':
          gsap.fromTo(
            element,
            {
              opacity: 0,
              ...options.from,
            },
            {
              opacity: 1,
              scrollTrigger: {
                trigger: options.trigger?.current || element,
                start: options.start || 'top 80%',
                end: options.end || 'bottom 20%',
                scrub: options.scrub || false,
                pin: options.pin || false,
              },
              ...options.to,
            },
          );
          break;

        case 'scaleUp':
          gsap.fromTo(
            element,
            { scale: 0.7, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              scrollTrigger: {
                trigger: options.trigger?.current || element,
                start: options.start || 'top top',
                scrub: options.scrub || 1,
                pin: options.pin || false,
              },
            },
          );
          break;

        case 'slideUp':
          gsap.fromTo(
            element,
            { y: 120, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              delay: options.delay || 0,
              scrollTrigger: {
                trigger: options.trigger?.current || element,
                start: options.start || 'top top',
                scrub: options.scrub || 1,
              },
            },
          );
          break;

        case 'stopMove':
          gsap.fromTo(
            element,
            { ...options.from },
            {
              scrollTrigger: {
                trigger: options.trigger?.current || element,
                start: options.start || 'top top',
                end: options.end || 'bottom bottom',
                scrub: options.scrub || 1,
                pin: options.pin || false,
              },
              ...options.to,
            },
          );
          break;
        /** TODO:삭제 예정 */
        case 'header':
          ScrollTrigger.create({
            trigger: document.documentElement,
            start: '100px top',
            onUpdate: (self) => {
              if (self.progress > 0.1) {
                element.classList.add('dark');
              } else {
                element.classList.remove('dark');
              }
            },
          });
          break;
        default:
          break;
      }
    });

    return () => ctx.revert();
  }, [type, options]);

  return ref;
}
